import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtNum = (n) => (n ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

const StatCard = ({ label, value, testId }) => (
  <div className="p-3 rounded-lg border bg-stone-50 border-stone-200 text-center" data-testid={testId}>
    <p className="text-xl font-bold text-stone-900">{value}</p>
    <p className="text-xs text-stone-500">{label}</p>
  </div>
);

const WmsImportDialog = ({ open, onClose, onImported }) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setStep(1); setPreview(null); setResult(null); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/wms/import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setPreview(r.data);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella lettura del file");
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/wms/import/confirm`, { session_id: preview.session_id }, { timeout: 180000 });
      setResult(r.data);
      setStep(3);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'import");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#8A1C2A]" />Importa Tracciabilità da Etifood</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-stone-600">
              Carica l'export Excel <span className="font-mono">Tracciabilità</span> di Etifood: contiene carichi, trasformazioni, scarti e vendite.
              Le righe duplicate dall'export e i movimenti già importati vengono gestiti automaticamente (puoi ricaricare periodi sovrapposti senza doppioni).
            </p>
            <label className="flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:border-[#8A1C2A]/50 hover:bg-stone-50 transition-colors" data-testid="wms-dropzone">
              <Upload className="w-8 h-8 text-stone-400" />
              <span className="text-sm font-medium text-stone-700">{busy ? "Lettura del file…" : "Clicca per scegliere il file .xls / .xlsx"}</span>
              <input type="file" accept=".xls,.xlsx" className="hidden" disabled={busy} onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="wms-file-input" />
            </label>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatCard label="Movimenti unici" value={preview.movements_count} testId="wms-prev-movements" />
              <StatCard label="Nuovi da importare" value={preview.new_count} testId="wms-prev-new" />
              <StatCard label="Già presenti" value={preview.existing_count} testId="wms-prev-existing" />
              <StatCard label="Partite coinvolte" value={preview.partite_count} testId="wms-prev-partite" />
            </div>
            <p className="text-sm text-stone-600">
              Periodo: <b>{fmtDate(preview.date_from)}</b> → <b>{fmtDate(preview.date_to)}</b> · {preview.rows_raw} righe nel file
              ({preview.duplicates_removed} duplicati export scartati, {preview.rows_lotto_prodotto} righe "LOTTO PRODOTTO" ignorate)
            </p>
            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left p-2 text-xs font-semibold text-stone-600">Tipo movimento</th>
                    <th className="text-right p-2 text-xs font-semibold text-stone-600">Righe</th>
                    <th className="text-right p-2 text-xs font-semibold text-stone-600">Kg carico</th>
                    <th className="text-right p-2 text-xs font-semibold text-stone-600">Kg scarico</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.by_type.map((t) => (
                    <tr key={t.causale} className="border-t border-stone-100" data-testid={`wms-prev-type-${t.causale}`}>
                      <td className="p-2 font-medium">{t.label} <span className="text-xs text-stone-400 font-mono">({t.causale})</span></td>
                      <td className="p-2 text-right">{t.count}</td>
                      <td className="p-2 text-right text-emerald-700">{t.kg_in > 0 ? fmtNum(t.kg_in) : "—"}</td>
                      <td className="p-2 text-right text-red-700">{t.kg_out > 0 ? fmtNum(t.kg_out) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.orphan_count > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm" data-testid="wms-orphan-warning">
                <p className="font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />{preview.orphan_count} partite scaricate senza carico registrato</p>
                <p className="text-amber-700 mt-1">
                  Il loro carico è precedente al periodo esportato: per giacenze complete rifai l'export partendo da più indietro e importalo (i movimenti già presenti non verranno duplicati).
                  Es.: {preview.orphan_examples.slice(0, 5).join(", ")}{preview.orphan_count > 5 ? "…" : ""}
                </p>
              </div>
            )}
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="w-6 h-6" /><p className="font-semibold">Import completato</p></div>
            <div className="grid grid-cols-2 gap-2" data-testid="wms-result-cards">
              <StatCard label="Movimenti importati" value={result.inserted} testId="wms-res-inserted" />
              <StatCard label="Saltati (già presenti)" value={result.skipped_existing} testId="wms-res-skipped" />
              <StatCard label="Genealogie aggiornate" value={result.links_updated} testId="wms-res-links" />
              <StatCard label="Movimenti totali in archivio" value={result.movements_total} testId="wms-res-total" />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 2 && <Button variant="outline" onClick={reset} data-testid="wms-back-btn">Indietro</Button>}
          {step === 2 && (
            preview?.new_count === 0 ? (
              <Button disabled variant="outline" data-testid="wms-confirm-btn">Nessun nuovo movimento da importare</Button>
            ) : (
              <Button onClick={confirm} disabled={busy} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="wms-confirm-btn">
                {busy ? "Import in corso…" : `Importa ${preview?.new_count ?? ""} movimenti`}
              </Button>
            )
          )}
          {step === 3 && <Button onClick={close} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="wms-done-btn">Chiudi</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WmsImportDialog;
